import { Link } from 'react-router-dom'
import { Shield, Eye, Lock, Database, UserX, FileText, CheckCircle } from 'lucide-react'
import SiteHeader from '../components/SiteHeader'
import SiteFooter from '../components/SiteFooter'

const brandFont = {
  fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", -apple-system, sans-serif',
  fontWeight: 100,
} as const

const bodyFont = {
  fontFamily: '"Helvetica Neue", sans-serif',
  fontWeight: 300,
} as const

function GlassCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border bg-white/[0.055] border-white/[0.09] backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/[0.14] transition-all duration-300 ${className}`}>
      {children}
    </div>
  )
}

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-consciousness-deep">
      <SiteHeader />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="mb-6 flex justify-center">
            <Shield className="w-16 h-16 text-trust-blue" strokeWidth={1} />
          </div>
          <h1
            className="text-5xl md:text-7xl tracking-[0.15em] mb-6 text-white"
            style={brandFont}
          >
            Privacy Policy
          </h1>
          <p className="text-xl text-white/70 max-w-3xl mx-auto mb-4" style={bodyFont}>
            How we protect your data and respect your privacy
          </p>
          <p className="text-sm text-white/40" style={bodyFont}>
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </section>

      {/* Quick Summary */}
      <section className="py-8 px-6">
        <div className="max-w-5xl mx-auto">
          <GlassCard>
            <div className="p-8">
              <h2 className="text-2xl tracking-wide mb-6 text-trust-blue flex items-center gap-3" style={{ ...bodyFont, fontWeight: 300 }}>
                <Eye className="w-6 h-6" />
                Privacy at a Glance
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  'We collect only what\'s necessary for service operation',
                  'Your data is encrypted in transit and at rest',
                  'We never sell your personal information',
                  'You can delete your data at any time',
                ].map((text) => (
                  <div key={text} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-verified-green flex-shrink-0 mt-1" />
                    <p className="text-white/70" style={bodyFont}>{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </GlassCard>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 px-6 pb-24">
        <div className="max-w-5xl mx-auto space-y-8">

          {/* Information We Collect */}
          <GlassCard>
            <div className="p-8">
              <h2 className="text-2xl tracking-wide mb-6 text-white flex items-center gap-3" style={{ ...bodyFont, fontWeight: 300 }}>
                <Database className="w-6 h-6 text-trust-blue" />
                Information We Collect
              </h2>
              <div className="space-y-6 text-white/70" style={bodyFont}>
                <div>
                  <h3 className="text-lg text-white mb-3" style={{ fontWeight: 400 }}>Account Information</h3>
                  <p className="leading-relaxed">
                    When you create a LUKHAS account, we collect your email address, username, and authentication
                    credentials. For LiD (Lambda ID) authentication, we also process identity verification data.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg text-white mb-3" style={{ fontWeight: 400 }}>Usage Data</h3>
                  <p className="leading-relaxed">
                    We collect information about how you interact with our consciousness technology platform, including
                    API calls, feature usage, model interactions, and performance metrics.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg text-white mb-3" style={{ fontWeight: 400 }}>Technical Information</h3>
                  <p className="leading-relaxed">
                    We automatically collect device information, IP addresses, browser types, and operating system
                    details for security monitoring and service optimization.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg text-white mb-3" style={{ fontWeight: 400 }}>Consciousness Interaction Data</h3>
                  <p className="leading-relaxed">
                    When you interact with LUKHAS consciousness systems, we may collect prompts, responses, and
                    interaction patterns. You can opt out in your account settings.
                  </p>
                </div>
              </div>
            </div>
          </GlassCard>

          {/* How We Use Information */}
          <GlassCard>
            <div className="p-8">
              <h2 className="text-2xl tracking-wide mb-6 text-white flex items-center gap-3" style={{ ...bodyFont, fontWeight: 300 }}>
                <Eye className="w-6 h-6 text-trust-blue" />
                How We Use Your Information
              </h2>
              <div className="space-y-4 text-white/70" style={bodyFont}>
                {[
                  { label: 'Service Delivery', desc: 'To provide and maintain LUKHAS consciousness technology services' },
                  { label: 'Personalization', desc: 'To customize your experience and adapt consciousness workflows' },
                  { label: 'Communication', desc: 'To send service updates, security alerts, and feature announcements' },
                  { label: 'Security', desc: 'To detect and prevent fraud, abuse, and unauthorized access' },
                  { label: 'Research', desc: 'To improve consciousness models and develop new features (with your consent)' },
                  { label: 'Legal Compliance', desc: 'To comply with applicable laws, regulations, and legal obligations' },
                ].map(({ label, desc }) => (
                  <div key={label} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-trust-blue flex-shrink-0 mt-1" />
                    <p><strong className="text-white" style={{ fontWeight: 400 }}>{label}:</strong> {desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </GlassCard>

          {/* Data Sharing */}
          <GlassCard>
            <div className="p-8">
              <h2 className="text-2xl tracking-wide mb-6 text-white flex items-center gap-3" style={{ ...bodyFont, fontWeight: 300 }}>
                <Lock className="w-6 h-6 text-trust-blue" />
                Data Sharing and Disclosure
              </h2>
              <div className="space-y-6 text-white/70" style={bodyFont}>
                <p className="leading-relaxed text-lg text-white" style={{ fontWeight: 400 }}>
                  We do not sell, rent, or trade your personal information.
                </p>
                {[
                  { title: 'Service Providers', desc: 'We work with trusted third-party service providers bound to protect your data.' },
                  { title: 'Legal Requirements', desc: 'We may disclose information if required by law, subpoena, or court order.' },
                  { title: 'Business Transfers', desc: 'In the event of a merger or acquisition, user information may be transferred with notice.' },
                ].map(({ title, desc }) => (
                  <div key={title}>
                    <h3 className="text-lg text-white mb-2" style={{ fontWeight: 400 }}>{title}</h3>
                    <p className="leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </GlassCard>

          {/* Data Security */}
          <GlassCard>
            <div className="p-8">
              <h2 className="text-2xl tracking-wide mb-6 text-white flex items-center gap-3" style={{ ...bodyFont, fontWeight: 300 }}>
                <Shield className="w-6 h-6 text-trust-blue" />
                Data Security
              </h2>
              <div className="space-y-4 text-white/70" style={bodyFont}>
                <p className="leading-relaxed">We implement industry-standard security measures:</p>
                <ul className="space-y-3 list-disc list-inside">
                  <li>End-to-end encryption (TLS 1.3) and AES-256 at rest</li>
                  <li>Regular security audits and penetration testing</li>
                  <li>Multi-factor authentication via LiD</li>
                  <li>Secure data centers with 24/7 monitoring</li>
                  <li>Incident response and breach notification procedures</li>
                </ul>
              </div>
            </div>
          </GlassCard>

          {/* Your Rights */}
          <GlassCard>
            <div className="p-8">
              <h2 className="text-2xl tracking-wide mb-6 text-white flex items-center gap-3" style={{ ...bodyFont, fontWeight: 300 }}>
                <UserX className="w-6 h-6 text-trust-blue" />
                Your Privacy Rights
              </h2>
              <div className="space-y-4 text-white/70" style={bodyFont}>
                <p className="leading-relaxed">You have the following rights:</p>
                {['Access', 'Rectification', 'Erasure', 'Portability', 'Object', 'Withdraw Consent'].map((right) => (
                  <div key={right} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-trust-blue flex-shrink-0 mt-1" />
                    <strong className="text-white" style={{ fontWeight: 400 }}>{right}</strong>
                  </div>
                ))}
                <p className="leading-relaxed mt-6">
                  Contact us at{' '}
                  <a href="mailto:privacy@lukhas.com" className="text-trust-blue hover:underline">
                    privacy@lukhas.com
                  </a>
                </p>
              </div>
            </div>
          </GlassCard>

          {/* International Transfers */}
          <GlassCard>
            <div className="p-8">
              <h2 className="text-2xl tracking-wide mb-6 text-white" style={{ ...bodyFont, fontWeight: 300 }}>
                International Data Transfers
              </h2>
              <div className="space-y-4 text-white/70" style={bodyFont}>
                <p className="leading-relaxed">
                  We ensure appropriate safeguards for international transfers including EU Standard Contractual Clauses,
                  UK International Data Transfer Addendum, and Data Processing Agreements with all partners.
                </p>
              </div>
            </div>
          </GlassCard>

          {/* Changes to Policy */}
          <GlassCard>
            <div className="p-8">
              <h2 className="text-2xl tracking-wide mb-6 text-white flex items-center gap-3" style={{ ...bodyFont, fontWeight: 300 }}>
                <FileText className="w-6 h-6 text-trust-blue" />
                Changes to This Policy
              </h2>
              <div className="space-y-4 text-white/70" style={bodyFont}>
                <p className="leading-relaxed">
                  We will notify you of material changes via email and in-app notification.
                  Continued use constitutes acceptance.
                </p>
              </div>
            </div>
          </GlassCard>

          {/* Contact */}
          <GlassCard>
            <div className="p-8 text-center">
              <h2 className="text-2xl tracking-wide mb-4 text-white" style={{ ...bodyFont, fontWeight: 300 }}>
                Questions About Privacy?
              </h2>
              <p className="text-white/50 mb-6" style={bodyFont}>
                Contact our Data Protection Officer
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <a
                  href="mailto:privacy@lukhas.com"
                  className="px-6 py-2.5 rounded-full bg-trust-blue text-white text-sm tracking-[0.06em] hover:bg-trust-blue/90 transition-all inline-block"
                  style={bodyFont}
                >
                  privacy@lukhas.com
                </a>
                <Link
                  to="/contact"
                  className="px-6 py-2.5 rounded-full border border-trust-blue/40 text-trust-blue text-sm tracking-[0.06em] hover:border-trust-blue/70 transition-all inline-block"
                  style={bodyFont}
                >
                  Contact Form
                </Link>
              </div>
            </div>
          </GlassCard>

        </div>
      </section>

      <SiteFooter
        columns={[
          {
            title: 'Legal',
            links: [
              { label: 'Privacy Policy', href: '/privacy' },
              { label: 'Terms of Service', href: '/terms' },
              { label: 'Cookie Policy', href: '/cookies' },
            ],
          },
          {
            title: 'Platform',
            links: [
              { label: 'Home', href: '/' },
              { label: 'Solutions', href: '/solutions' },
              { label: 'Contact', href: '/contact' },
            ],
          },
          {
            title: 'Ecosystem',
            links: [
              { label: 'Developers', href: 'https://lukhas.dev', external: true },
              { label: 'App Store', href: 'https://lukhas.store', external: true },
            ],
          },
        ]}
      />
    </div>
  )
}
